import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '../hooks/useAuth0';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider
} from '@mui/material';
import {
  Flight,
  Hotel,
  DirectionsCar,
  CalendarToday,
  LocationOn,
  CheckCircle,
  Pending,
  Cancel,
  Add,
  ArrowForward
} from '@mui/icons-material';
import api from '../services/api';

const BookingsList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: '/travel/bookings' } });
    } else {
      fetchBookings();
    }
  }, [isAuthenticated, loginWithRedirect]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/travel/bookings');
      setBookings(response.data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'success';
      case 'PENDING': return 'warning';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CONFIRMED': return <CheckCircle />;
      case 'PENDING': return <Pending />;
      case 'CANCELLED': return <Cancel />;
      default: return null;
    }
  };

  const getTierColor = (tierCode) => {
    switch (tierCode) {
      case 'DIGITAL': return '#1A5EA8';
      case 'CONCIERGE': return '#10b981';
      case 'WHITE_GLOVE': return '#f59e0b';
      default: return '#1A5EA8';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!isAuthenticated || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={fetchBookings} sx={{ mt: 2 }}>Try Again</Button>
      </Container>
    );
  }

  if (bookings.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Flight sx={{ fontSize: 80, color: '#1A5EA8', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            No Travel Bookings Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Start planning your journey to clinical trial sites
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate('/travel/book')}
            sx={{ mt: 2, bgcolor: '#1A5EA8', px: 4, '&:hover': { bgcolor: '#5568d3' } }}
          >
            Book Travel
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f9fafb', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
              My Travel Bookings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your clinical trial travel arrangements
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/travel/book')}
            sx={{ bgcolor: '#1A5EA8', '&:hover': { bgcolor: '#5568d3' } }}
          >
            New Booking
          </Button>
        </Box>

        {/* Bookings Grid */}
        <Grid container spacing={3}>
          {bookings.map((booking) => (
            <Grid item xs={12} md={6} lg={4} key={booking.id}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Status + Tier */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip
                      icon={getStatusIcon(booking.bookingStatus)}
                      label={booking.bookingStatus}
                      color={getStatusColor(booking.bookingStatus)}
                      size="small"
                    />
                    <Chip
                      label={booking.serviceTier?.replace('_', ' ')}
                      size="small"
                      sx={{ bgcolor: getTierColor(booking.serviceTier), color: 'white', fontWeight: 600 }}
                    />
                  </Box>

                  {/* Booking Reference */}
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Ref: {booking.bookingReference}
                  </Typography>

                  {/* Trial Name */}
                  {booking.trialName && (
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      {booking.trialName.substring(0, 60)}
                      {booking.trialName.length > 60 ? '...' : ''}
                    </Typography>
                  )}

                  {/* Destination */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <LocationOn sx={{ fontSize: 20, color: '#1A5EA8', mr: 1, mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {booking.trialSiteName || 'Trial Site'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {booking.destinationCity}, {booking.destinationState}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Trip Dates */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarToday sx={{ fontSize: 20, color: '#1A5EA8', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(booking.tripStartDate)} — {formatDate(booking.tripEndDate)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Flight / Hotel / Car Status */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Flight sx={{ fontSize: 18, color: booking.flightBookingId ? '#10b981' : '#9ca3af', mr: 0.5 }} />
                      <Typography variant="caption" color={booking.flightBookingId ? 'success.main' : 'text.secondary'}>
                        {booking.flightBookingId ? 'Booked' : 'No flight'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Hotel sx={{ fontSize: 18, color: booking.hotelBookingId ? '#10b981' : '#9ca3af', mr: 0.5 }} />
                      <Typography variant="caption" color={booking.hotelBookingId ? 'success.main' : 'text.secondary'}>
                        {booking.hotelBookingId ? 'Booked' : 'No hotel'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DirectionsCar sx={{ fontSize: 18, color: booking.groundTransportType ? '#10b981' : '#9ca3af', mr: 0.5 }} />
                      <Typography variant="caption" color={booking.groundTransportType ? 'success.main' : 'text.secondary'}>
                        {booking.groundTransportType ? 'Booked' : 'No car'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Total Amount */}
                  <Box sx={{ bgcolor: '#f9fafb', p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: getTierColor(booking.serviceTier) }}>
                        ${booking.totalAmount?.toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Payment: {booking.paymentStatus}
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate(`/travel/bookings/${booking.id}`)}
                    sx={{
                      borderColor: getTierColor(booking.serviceTier),
                      color: getTierColor(booking.serviceTier),
                      '&:hover': {
                        borderColor: getTierColor(booking.serviceTier),
                        bgcolor: 'rgba(102, 126, 234, 0.1)'
                      }
                    }}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default BookingsList;